import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchEvaluations, saveEvaluation, changeEvaluationStatus } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import Swal from 'sweetalert2';
import useButtonAccess from "components/Common/useButtonAccess";

const EvaluationMaster = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const { evaluationsList, loading, success, error } = useSelector((state) => state.EvaluationMasterReducer || {});
  const [authDetails, setAuthDetails] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0]);
    const unitId = authUserDetails[0].unit_id;
    dispatch(fetchEvaluations(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchEvaluations(authDetails?.unit_id));
      setIsEdit(false);
      setEditId(null);
      formik.resetForm();
    }
  }, [authDetails, success, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      evaluation_id: 0,
      evaluation_type: "",
      description: ""
    },
    validationSchema: Yup.object({
      evaluation_type: Yup.string().required("Evaluation Type is required")
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("evaluation_id", values.evaluation_id);
      formData.append("evaluation_type", values.evaluation_type);
      formData.append("description", values.description);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);

      dispatch(saveEvaluation(formData));
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      evaluation_id: row.evaluation_id,
      evaluation_type: row.evaluation_type,
      description: row.description
    });
    setIsEdit(true);
    setEditId(row.evaluation_id);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEditId(null);
  };

  const handleToggleStatus = (row) => {
    Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete ${row.evaluation_type}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("evaluation_id", row.evaluation_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeEvaluationStatus(formData));
      }
    });
  };

  const columns = useMemo(() => {
    const actionColumn = (canEdit || canDelete)
      ? {
          Header: "Action",
          className: "status-col",
          applyHeaderClass: true,
          Cell: (cellProps) => {
            const { evaluation_id } = cellProps.row.original;
            return (
              <div className="d-flex gap-3">
                {canEdit && (
                  <Link
                    to="#"
                    className="text-success"
                    onClick={() => handleEdit(cellProps.row.original)}
                  >
                    <i
                      className="bx bxs-edit font-size-18"
                      id={`edit-tooltip-${evaluation_id}`}
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target={`edit-tooltip-${evaluation_id}`}
                    >
                      Edit
                    </UncontrolledTooltip>
                  </Link>
                )}

                {canDelete && (
                  <Link
                    to="#"
                    className="text-danger"
                    onClick={() => handleToggleStatus(cellProps.row.original)}
                  >
                    <i
                      className="mdi mdi-delete font-size-18"
                      id={`delete-tooltip-${evaluation_id}`}
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target={`delete-tooltip-${evaluation_id}`}
                    >
                      Delete
                    </UncontrolledTooltip>
                  </Link>
                )}
              </div>
            );
          },
        }
      : null;

    return [
      ...(actionColumn ? [actionColumn] : []),
      {
        Header: "S.No",
        Cell: ({ row }) => evaluationsList.length - row.index,
        className: "serial-no",
        applyHeaderClass: true,
      },
      { Header: "Evaluation Type", accessor: "evaluation_type" },
      { Header: "Description", accessor: "description" },
    ];
  }, [evaluationsList, canEdit, canDelete]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Setup" breadcrumbItem="Evaluation Master" />

          {(canCreate || canEdit) && (
            <Card>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <Row className="mb-3 g-3">
                    <Col md="6">
                      <FormGroup>
                        <Label for="evaluation_type">
                          Evaluation Type <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="evaluation_type"
                          name="evaluation_type"
                          type="text"
                          value={formik.values.evaluation_type}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.evaluation_type &&
                            Boolean(formik.errors.evaluation_type)
                          }
                        />
                        {formik.touched.evaluation_type && formik.errors.evaluation_type && (
                          <div className="text-danger">{formik.errors.evaluation_type}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="description">
                          Description
                        </Label>
                        <Input
                          id="description"
                          name="description"
                          type="text"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.description &&
                            Boolean(formik.errors.description)
                          }
                        />
                        {formik.touched.description && formik.errors.description && (
                          <div className="text-danger">{formik.errors.description}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="d-flex justify-content-end gap-2">
                      <Button type="submit" color="primary" className="btn button-save">
                        <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                      </Button>
                      <Button type="button" color="secondary" onClick={handleCancel} className="btn button-cancel">
                        <i className="fas fa-sync me-1" /> Cancel
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={evaluationsList && evaluationsList?.length > 0 ? evaluationsList : []}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EvaluationMaster;
